import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Space,
  Tag,
  Descriptions,
  Timeline,
  List,
  Checkbox,
  Progress,
  Spin,
  App as AntdApp,
  Grid,
  Input,
  Badge,
  Alert,
  Statistic,
  Tabs,
} from 'antd';
import { useBreadcrumbTitle } from '../hooks/useBreadcrumb';
import {
  ArrowLeftOutlined,
  AlertOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined,
  TeamOutlined,
  FireOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { trpc } from '../lib/api';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface EmergencyWorkChecklistItem {
  id: string;
  itemText: string;
  isCompleted: boolean;
  completedAt?: string | null;
}

interface EmergencyWorkLogEntry {
  id: string;
  action: string;
  description: string;
  createdAt: string;
  oldStatus?: string | null;
  newStatus?: string | null;
}

export function EmergencyWorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { message: messageApi } = AntdApp.useApp();

  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true); // 緊急作業はデフォルトでON
  const [elapsedTime, setElapsedTime] = useState(0);
  const detailColumnCount = screens.lg ? 2 : 1;
  const detailFullSpan = detailColumnCount;

  // 緊急作業詳細取得
  const {
    data: work,
    isLoading,
    refetch: refetchWork
  } = trpc.emergencyWork.getById.useQuery(id || '', {
    enabled: !!id,
  });

  // チェックリスト取得
  const { data: checklist, refetch: refetchChecklist } = trpc.emergencyWork.getChecklist.useQuery(
    id || '',
    { enabled: !!id }
  );

  // ログ取得
  const { data: logs } = trpc.emergencyWork.getLogs.useQuery(
    id || '',
    { enabled: !!id }
  );

  const checklistItems: EmergencyWorkChecklistItem[] = Array.isArray(checklist) ? (checklist as EmergencyWorkChecklistItem[]) : [];
  const logEntries: EmergencyWorkLogEntry[] = Array.isArray(logs) ? (logs as EmergencyWorkLogEntry[]) : [];
  const workWithRelations = work as (typeof work & {
    equipment?: { name?: string | null } | null;
    location?: { name?: string | null } | null;
    assignedUser?: { name?: string | null } | null;
  }) | undefined;

  // パンくずリストに緊急作業タイトルを設定
  useBreadcrumbTitle('emergency-work', work?.title);

  // Mutations
  const addChecklistMutation = trpc.emergencyWork.addChecklistItem.useMutation({
    onSuccess: () => {
      messageApi.success('チェックリスト項目を追加しました');
      setNewChecklistItem('');
      refetchChecklist();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    }
  });

  const updateChecklistMutation = trpc.emergencyWork.updateChecklistItem.useMutation({
    onSuccess: () => {
      refetchChecklist();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    }
  });

  const startMutation = trpc.emergencyWork.start.useMutation({
    onSuccess: () => {
      messageApi.success('作業を開始しました');
      refetchWork();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    }
  });

  const completeMutation = trpc.emergencyWork.complete.useMutation({
    onSuccess: () => {
      messageApi.success('作業を完了しました');
      navigate('/app/emergency-works');
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    }
  });

  // 自動更新（緊急作業は10秒間隔）
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refetchWork();
        refetchChecklist();
      }, 10 * 1000); // 10秒
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refetchWork, refetchChecklist]);

  // 経過時間の計算
  useEffect(() => {
    if (work?.startedAt) {
      const interval = setInterval(() => {
        const started = new Date(work.startedAt).getTime();
        const now = Date.now();
        setElapsedTime(Math.floor((now - started) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [work?.startedAt]);

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      addChecklistMutation.mutate({
        workId: id || '',
        item: {
          itemText: newChecklistItem.trim()
        }
      });
    }
  };

  const handleChecklistChange = (itemId: string, checked: boolean) => {
    updateChecklistMutation.mutate({
      itemId,
      isCompleted: checked
    });
  };

  const handleStart = () => {
    startMutation.mutate(id || '');
  };

  const handleComplete = () => {
    completeMutation.mutate({
      id: id || '',
      notes: completionNotes
    });
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      critical: 'red',
      high: 'orange',
      medium: 'blue',
      low: 'default',
    };
    return colors[priority] || 'default';
  };

  type BadgeStatus = 'success' | 'processing' | 'default' | 'error' | 'warning';

  const getStatusColor = (status: string): BadgeStatus => {
    const colors: Record<string, BadgeStatus> = {
      reported: 'default',
      assigned: 'processing',
      in_progress: 'warning',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] ?? 'default';
  };

  const formatElapsedTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const completedCount = checklistItems.filter(item => item.isCompleted).length;
  const totalCount = checklistItems.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!work) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Title level={4}>緊急作業が見つかりません</Title>
          <Button onClick={() => navigate('/app/emergency-works')}>一覧に戻る</Button>
        </Card>
      </div>
    );
  }

  const isPriorityHigh = work.priority === 'critical' || work.priority === 'high';

  const logTimelineItems = logEntries.map((log: EmergencyWorkLogEntry) => ({
    key: log.id,
    color:
      log.action === 'completed' ? 'green' :
        log.action === 'started' ? 'blue' :
          log.action === 'assigned' ? 'orange' :
            'gray',
    dot:
      log.action === 'completed' ? <CheckCircleOutlined /> :
        log.action === 'started' ? <ClockCircleOutlined /> :
          log.action === 'assigned' ? <UserOutlined /> :
            undefined,
    children: (
      <>
        <div>
          <strong>{log.description}</strong>
        </div>
        <div style={{ fontSize: '12px', color: '#999' }}>
          {moment(log.createdAt).format('YYYY-MM-DD HH:mm:ss')}
        </div>
        {log.oldStatus && log.newStatus && (
          <div style={{ fontSize: '12px', marginTop: 4 }}>
            <Tag>{log.oldStatus}</Tag> → <Tag>{log.newStatus}</Tag>
          </div>
        )}
      </>
    )
  }));

  const tabItems = [
    {
      key: 'detail',
      label: '詳細情報',
      children: (
        <Descriptions
          bordered
          column={detailColumnCount}
          styles={{ label: { fontWeight: 'bold' } }}
        >
          <Descriptions.Item label="タイトル" span={detailFullSpan}>
            {work.title}
          </Descriptions.Item>
          <Descriptions.Item label="説明" span={detailFullSpan}>
            {work.description || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="優先度">
            <Tag color={getPriorityColor(work.priority)}>
              {work.priority.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="ステータス">
            <Badge status={getStatusColor(work.status)} text={work.status} />
          </Descriptions.Item>
          <Descriptions.Item label="影響レベル">
            {work.impactLevel ? (
              <Tag color="red">{work.impactLevel.toUpperCase()}</Tag>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="予想ダウンタイム">
            {work.estimatedDowntime ? `${work.estimatedDowntime}分` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="関連設備">
            {workWithRelations?.equipment?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="発生場所">
            {workWithRelations?.location?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="影響範囲" span={detailFullSpan}>
            {work.affectedAreas || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="報告日時">
            {work.reportedAt && moment(work.reportedAt).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="完了予定">
            {work.estimatedCompletion && moment(work.estimatedCompletion).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="備考" span={detailFullSpan}>
            {work.notes || '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'checklist',
      label: `チェックリスト (${completedCount}/${totalCount})`,
      children: (
        <>
          <div style={{ marginBottom: 16 }}>
            <Progress percent={Math.round(progress)} status={progress === 100 ? 'success' : 'active'} />
          </div>

          <List
            dataSource={checklistItems}
            renderItem={(item: EmergencyWorkChecklistItem) => (
              <List.Item>
                <Checkbox
                  checked={item.isCompleted}
                  onChange={(e) => handleChecklistChange(item.id, e.target.checked)}
                  disabled={work.status === 'completed'}
                >
                  <span style={{
                    textDecoration: item.isCompleted ? 'line-through' : 'none',
                    color: item.isCompleted ? '#999' : '#000'
                  }}>
                    {item.itemText}
                  </span>
                </Checkbox>
                {item.completedAt && (
                  <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>
                    {moment(item.completedAt).format('MM/DD HH:mm')}
                  </span>
                )}
              </List.Item>
            )}
          />

          {work.status !== 'completed' && (
            <div style={{ marginTop: 16 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="新しいチェックリスト項目を追加"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onPressEnter={handleAddChecklistItem}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddChecklistItem}
                >
                  追加
                </Button>
              </Space.Compact>
            </div>
          )}
        </>
      ),
    },
    {
      key: 'logs',
      label: '作業ログ',
      children: (
        <Timeline mode="left" items={logTimelineItems} />
      ),
    },
    ...(work.status === 'in_progress'
      ? [
        {
          key: 'complete',
          label: '作業完了',
          children: (
            <>
              <div style={{ marginBottom: 16 }}>
                <Title level={4}>作業完了報告</Title>
                <Paragraph>
                  作業の結果、対処内容、今後の対策などを記録してください。
                </Paragraph>
              </div>
              <TextArea
                rows={6}
                placeholder="作業内容、結果、所要時間、使用部品・消耗品、今後の推奨事項など"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  return (
    <div style={{ padding: '24px' }} className="emergency-work-detail-page">
      {/* 緊急度アラート */}
      {isPriorityHigh && (
        <Alert
          message={
            <Space>
              <FireOutlined />
              緊急度: {work.priority.toUpperCase()}
              {work.impactLevel && ` | 影響レベル: ${work.impactLevel.toUpperCase()}`}
            </Space>
          }
          description={work.affectedAreas}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
          icon={<ExclamationCircleOutlined />}
        />
      )}

      {/* ヘッダー */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Card className={isPriorityHigh ? 'emergency-header' : ''}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space direction="vertical" size={0}>
                  <Title level={2} style={{ margin: '8px 0', color: isPriorityHigh ? '#ff4d4f' : undefined }}>
                    <AlertOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
                    {work.title}
                  </Title>
                  <Space>
                    <Tag color={getPriorityColor(work.priority)}>
                      優先度: {work.priority.toUpperCase()}
                    </Tag>
                    <Badge status={getStatusColor(work.status)} text={work.status} />
                    {workWithRelations?.assignedUser && (
                      <Tag icon={<UserOutlined />}>
                        {workWithRelations.assignedUser.name}
                      </Tag>
                    )}
                  </Space>
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/app/emergency-works')}
                  >
                    戻る
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      refetchWork();
                      refetchChecklist();
                    }}
                    loading={autoRefresh}
                  >
                    更新
                  </Button>
                  <Button
                    type={autoRefresh ? 'primary' : 'default'}
                    onClick={() => setAutoRefresh(!autoRefresh)}
                  >
                    自動更新: {autoRefresh ? 'ON (10秒)' : 'OFF'}
                  </Button>
                  <Button
                    icon={<PhoneOutlined />}
                    danger
                  >
                    緊急連絡
                  </Button>
                  <Button
                    icon={<TeamOutlined />}
                    type="primary"
                  >
                    要員追加
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ステータスカード */}
      {work.status === 'in_progress' && (
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="経過時間"
                value={formatElapsedTime(elapsedTime)}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="予想ダウンタイム"
                value={work.estimatedDowntime || 0}
                suffix="分"
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="チェックリスト進捗"
                value={progress}
                suffix="%"
                valueStyle={{ color: progress === 100 ? '#52c41a' : '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic.Timer
                title="完了期限"
                value={work.estimatedCompletion ? new Date(work.estimatedCompletion).getTime() : Date.now() + 3600000}
                type="countdown"
                format="HH:mm:ss"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* メインコンテンツ */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Tabs defaultActiveKey="detail" items={tabItems} />
          </Card>
        </Col>
      </Row>

      {/* アクションボタン */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card>
            <Space wrap>
              {work.status === 'assigned' && (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleStart}
                  size="large"
                >
                  作業開始
                </Button>
              )}
              {work.status === 'in_progress' && (
                <Button
                  type="primary"
                  danger
                  icon={<StopOutlined />}
                  onClick={handleComplete}
                  size="large"
                >
                  作業完了
                </Button>
              )}
              <Button
                icon={<ExclamationCircleOutlined />}
                danger
                size="large"
              >
                エスカレーション
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 緊急作業用スタイル */}
      <style>{`
        .emergency-header {
          background: linear-gradient(135deg, #fff5f5 0%, #ffebeb 100%);
          border: 2px solid #ffccc7;
        }
        .emergency-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ff4d4f 0%, #ff7875 50%, #ff4d4f 100%);
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @media print {
          .ant-layout-header,
          .ant-layout-sider,
          .ant-breadcrumb,
          .ant-btn,
          .ant-tabs-nav {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
